import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../auth/AuthContext";
import ProfileLayout from "../components/ProfileLayout";
import { formatLocalTimestamp } from "../lib/dateLocal";
import { pageItems } from "../lib/pageItems";
import { LifeBuoy } from "lucide-react";
import { resolveMediaUrl } from "../lib/fileUrl";
import SupportThread from "../components/SupportThread";
import { toast } from "sonner";

const STATUS_COLORS = {
  open: "var(--zc-accent)",
  investigating: "var(--zc-warn,#f59e0b)",
  resolved: "var(--zc-success)",
  closed: "var(--zc-text-3)",
};

const CATEGORY_LABELS = {
  damage: "Damage",
  cleanliness: "Cleanliness",
  payment_refund: "Payment / refund",
  no_show: "No-show",
  fraud: "Fraud",
  other: "Other",
};

export default function ProfileSupport() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/support/cases/me`, { withCredentials: true });
      setCases(pageItems(r.data));
    } catch {
      setCases([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sendMessage = async (caseId, body, attachmentFileIds = []) => {
    try {
      const r = await axios.post(
        `${API}/support/cases/${caseId}/messages`,
        {
          body,
          ...(attachmentFileIds?.length ? { attachment_file_ids: attachmentFileIds } : {}),
        },
        { withCredentials: true },
      );
      setCases((prev) => prev.map((c) => (
        c.case_id === caseId
          ? {
              ...c,
              messages: [...(c.messages || []), r.data],
              status: c.status === "resolved" ? "open" : c.status,
            }
          : c
      )));
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail === "case_closed"
        ? "This case is closed"
        : (e.response?.data?.detail || "Could not send message"));
      return false;
    }
  };

  return (
    <ProfileLayout eyebrow="Profile" title="Support cases">
      <div className="zc-card overflow-hidden" data-testid="profile-support-list">
        {loading && (
          <div className="px-6 py-10 text-center text-[var(--zc-text-3)] text-sm">Loading…</div>
        )}
        {!loading && cases.length === 0 && (
          <div className="px-6 py-12 text-center text-[var(--zc-text-3)] text-sm">
            <LifeBuoy size={28} className="mx-auto mb-3 opacity-50" />
            No support cases yet. Report an issue from a booking if something went wrong.
            <div className="mt-4">
              <Link to="/profile/bookings" className="zc-btn-primary px-4 h-9 inline-grid place-items-center text-xs">
                My bookings
              </Link>
            </div>
          </div>
        )}
        {cases.map((c) => (
          <div
            key={c.case_id}
            className="px-4 sm:px-6 py-4 border-b border-[var(--zc-border)] last:border-0"
            data-testid={`support-case-${c.case_id}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-sm">{c.subject}</div>
                <div className="text-xs text-[var(--zc-text-3)] mt-1" data-testid={`support-case-id-${c.case_id}`}>
                  Case # <span className="font-mono">{c.case_id}</span>
                </div>
                <div className="text-xs text-[var(--zc-text-3)] mt-1">
                  {CATEGORY_LABELS[c.category] || c.category}
                  {c.booking_id && (
                    <> · Booking <span className="font-mono">{c.booking_id}</span></>
                  )}
                </div>
              </div>
              <span
                className="text-xs font-semibold px-2 py-1 rounded-full capitalize"
                style={{
                  background: "var(--zc-bg-3)",
                  color: STATUS_COLORS[c.status] || "var(--zc-text-2)",
                }}
              >
                {(c.status || "open").replace("_", " ")}
              </span>
            </div>
            <p className="text-sm text-[var(--zc-text-2)] mt-2 line-clamp-3">{c.body}</p>
            {c.attachment_file_ids?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3" data-testid={`support-case-attachments-${c.case_id}`}>
                {c.attachment_file_ids.map((fid) => {
                  const href = resolveMediaUrl(`/api/files/${fid}`);
                  return (
                    <a
                      key={fid}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-14 h-14 overflow-hidden rounded-md border border-[var(--zc-border)] hover:border-[var(--zc-accent)]"
                    >
                      <img src={href} alt="Attachment" className="w-full h-full object-cover" />
                    </a>
                  );
                })}
              </div>
            )}
            <div className="text-xs text-[var(--zc-text-3)] mt-2">
              Filed {formatLocalTimestamp(c.created_at) || c.created_at}
            </div>
            <SupportThread
              caseId={c.case_id}
              messages={c.messages || []}
              viewerRole="reporter"
              onSend={(body, attachmentFileIds) => sendMessage(c.case_id, body, attachmentFileIds)}
              disabled={c.status === "closed"}
              disabledReason="This case is closed. Open a new report if you need more help."
              testidPrefix="support-thread"
            />
          </div>
        ))}
      </div>
    </ProfileLayout>
  );
}
